// @intent: Runtime render-model orchestration for the isolated playable runtime
// @flow: RuntimeApp state -> RuntimeCanvas compiles RenderModel -> Pixi adapter renders loaded assets
// @stale: when runtime readiness or render-model error semantics change

import React from 'react';
import {
  collectRenderAssetUrls,
  compileProjectRenderModel,
  validateMatch3NativeRuntimePayloadV1,
  type AssetRef,
  type ButtonLayer,
  type GroupLayer,
  type JsonObject,
  type Layer,
  type Project,
  type RectLayer,
  type RenderModel,
  type RenderNode,
  type SceneLayer,
  type SlotMap,
  type SlotRef,
  type SpriteLayer,
} from '@mintyads/playable-api';
import type { RuntimeInputPayload } from '@mintyads/shared-types';
import { PixiRuntimeCanvas } from './pixi/PixiRuntimeCanvas';
import type { RuntimeMatch3Controls } from './pixi/match3DriveCapture';
import type { RuntimeProbeControls } from './probe/runtimeProbeTypes';
import { preloadPixiAssets, resolvePixiAssetUrl } from './pixi/pixiAssets';

interface RuntimeCanvasProps {
  project: Project | null;
  viewport: { readonly width: number; readonly height: number };
  highlightedLayerIds: readonly string[];
  onLayerSelected: (layerId: string | null) => void;
  onRuntimeError: (code: string, message: string) => void;
  onRuntimeAnalytics?: ((name: string, data?: JsonObject) => void) | undefined;
  onRuntimeInput?: ((payload: RuntimeInputPayload) => void) | undefined;
  onRuntimeControls?: ((controls: RuntimeMatch3Controls | null) => void) | undefined;
  onRuntimeProbeControls?: ((controls: RuntimeProbeControls | null) => void) | undefined;
  onProjectRendered: (payload: { projectId: string; layerCount: number; assetCount: number }) => void;
  onRuntimeFacts: (facts: RuntimeSnapshotFacts) => void;
  assetBaseUrl?: string | undefined;
}

/** @intent Carry renderer-observed facts back to RuntimeApp for queryState snapshots. @flow RuntimeCanvas compiles/render-checks model -> RuntimeApp posts runtime.stateSnapshot. @stale Keep aligned with RuntimeVerifierSnapshot and playable runtime protocol. */
export interface RuntimeSnapshotFacts {
  readonly layerIds: readonly string[];
  readonly mountedLayerIds: readonly string[];
  readonly assetUrls: readonly string[];
  readonly errorCount: number;
}

type RuntimeRenderedPayload = {
  projectId: string;
  layerCount: number;
  assetCount: number;
};

type ReportedRuntimeState = {
  readonly kind: 'error' | 'rendered';
  readonly key: string;
};

function resolveRuntimeAssetUrl(url: string, assetBaseUrl: string | undefined): string {
  return resolvePixiAssetUrl(url, assetBaseUrl);
}

function resolveSpriteLayerAssetUrl(layer: SpriteLayer, assetBaseUrl: string | undefined): SpriteLayer {
  const assetUrl = resolveRuntimeAssetUrl(layer.assetUrl, assetBaseUrl);
  return assetUrl === layer.assetUrl ? layer : { ...layer, assetUrl };
}

function resolveButtonLayerAssetUrl(layer: ButtonLayer, assetBaseUrl: string | undefined): ButtonLayer {
  if (!layer.assetUrl) return layer;
  const assetUrl = resolveRuntimeAssetUrl(layer.assetUrl, assetBaseUrl);
  return assetUrl === layer.assetUrl ? layer : { ...layer, assetUrl };
}

function resolveRectLayerAssetUrl(layer: RectLayer, assetBaseUrl: string | undefined): RectLayer {
  if (!layer.assetUrl) return layer;
  const assetUrl = resolveRuntimeAssetUrl(layer.assetUrl, assetBaseUrl);
  return assetUrl === layer.assetUrl ? layer : { ...layer, assetUrl };
}

function resolveSceneLayerAssetUrls(layer: SceneLayer, assetBaseUrl: string | undefined): SceneLayer {
  const children = layer.children.map((child) => {
    const next = resolveLayerAssetUrls(child, assetBaseUrl);
    return next;
  });
  const changed = children.some((child, index) => child !== layer.children[index]);
  return changed ? { ...layer, children } : layer;
}

function resolveGroupLayerAssetUrls(layer: GroupLayer, assetBaseUrl: string | undefined): GroupLayer {
  const children = layer.children.map((child) => {
    const next = resolveLayerAssetUrls(child, assetBaseUrl);
    return next;
  });
  const changed = children.some((child, index) => child !== layer.children[index]);
  return changed ? { ...layer, children } : layer;
}

function resolveLayerAssetUrls(layer: Layer, assetBaseUrl: string | undefined): Layer {
  switch (layer.type) {
    case 'sprite':
      return resolveSpriteLayerAssetUrl(layer, assetBaseUrl);
    case 'scene':
      return resolveSceneLayerAssetUrls(layer, assetBaseUrl);
    case 'group':
      return resolveGroupLayerAssetUrls(layer, assetBaseUrl);
    case 'button':
      return resolveButtonLayerAssetUrl(layer, assetBaseUrl);
    case 'rect':
      return resolveRectLayerAssetUrl(layer, assetBaseUrl);
    case 'text':
      return layer;
  }
}

function layerTreeHasId(layer: Layer, id: string): boolean {
  if (layer.id === id) return true;
  if (layer.type !== 'scene' && layer.type !== 'group') return false;
  return layer.children.some((child) => layerTreeHasId(child, id));
}

function nativeMatch3CtaShellLayer(project: Project, assetUrl: string): SpriteLayer {
  const width = Math.max(48, Math.min(220, project.manifest.viewport.width - 48));
  return {
    id: 'native-match3-cta',
    name: 'CTA',
    type: 'sprite',
    visible: false,
    locked: false,
    x: Math.max(0, (project.manifest.viewport.width - width) / 2),
    y: Math.max(0, project.manifest.viewport.height - 128),
    width,
    height: 72,
    assetUrl,
  };
}

function normalizeNativeMatch3SceneShell(project: Project): Project {
  const parsed = validateMatch3NativeRuntimePayloadV1(project.variables.nativeMatch3);
  if (!parsed.ok) return project;

  const ctaSlot = project.slot_map.slots['endcard.cta'];
  if (ctaSlot?.layerId !== 'native-match3-cta') return project;
  if (layerTreeHasId(project.scene_graph.root, 'native-match3-cta')) return project;

  return {
    ...project,
    scene_graph: {
      ...project.scene_graph,
      root: {
        ...project.scene_graph.root,
        children: [
          ...project.scene_graph.root.children,
          nativeMatch3CtaShellLayer(project, ctaSlot.assetUrl ?? parsed.value.skin.slots.ctaButton),
        ],
      },
    },
  };
}

function resolveAssetRefUrl(asset: AssetRef, assetBaseUrl: string | undefined): AssetRef {
  const url = resolveRuntimeAssetUrl(asset.url, assetBaseUrl);
  return url === asset.url ? asset : { ...asset, url };
}

function resolveSlotAssetUrl(slot: SlotRef, assetBaseUrl: string | undefined): SlotRef {
  if (!slot.assetUrl) return slot;
  const assetUrl = resolveRuntimeAssetUrl(slot.assetUrl, assetBaseUrl);
  return assetUrl === slot.assetUrl ? slot : { ...slot, assetUrl };
}

function resolveSlotMapAssetUrls(slotMap: SlotMap, assetBaseUrl: string | undefined): SlotMap {
  let slots: Record<string, SlotRef> | null = null;
  for (const [slotId, slot] of Object.entries(slotMap.slots)) {
    const next = resolveSlotAssetUrl(slot, assetBaseUrl);
    if (next !== slot) {
      slots ??= { ...slotMap.slots };
      slots[slotId] = next;
    }
  }
  return slots ? { ...slotMap, slots } : slotMap;
}

function resolveNativeMatch3AssetUrls(value: unknown, assetBaseUrl: string | undefined): unknown {
  const parsed = validateMatch3NativeRuntimePayloadV1(value);
  if (!parsed.ok) return value;
  const levelTiles = parsed.value.level.tiles.map((tile) => {
    const assetUrl = resolveRuntimeAssetUrl(tile.assetUrl, assetBaseUrl);
    return assetUrl === tile.assetUrl ? tile : { ...tile, assetUrl };
  });
  const slots = parsed.value.skin.slots;
  return {
    version: 'v1',
    level: {
      ...parsed.value.level,
      tiles: levelTiles,
    },
    skin: {
      ...parsed.value.skin,
      slots: {
        ...slots,
        background: resolveRuntimeAssetUrl(slots.background, assetBaseUrl),
        boardPanel: resolveRuntimeAssetUrl(slots.boardPanel, assetBaseUrl),
        tileTypes: slots.tileTypes.map((url) => resolveRuntimeAssetUrl(url, assetBaseUrl)),
        objectiveStages: slots.objectiveStages.map((url) => resolveRuntimeAssetUrl(url, assetBaseUrl)),
        tutorialHand: resolveRuntimeAssetUrl(slots.tutorialHand, assetBaseUrl),
        hudPanel: resolveRuntimeAssetUrl(slots.hudPanel, assetBaseUrl),
        ctaButton: resolveRuntimeAssetUrl(slots.ctaButton, assetBaseUrl),
        ...(slots.logo === undefined ? {} : { logo: resolveRuntimeAssetUrl(slots.logo, assetBaseUrl) }),
        ...(slots.reward === undefined ? {} : { reward: resolveRuntimeAssetUrl(slots.reward, assetBaseUrl) }),
      },
    },
  };
}

function resolveProjectVariablesForRuntime(
  variables: Project['variables'],
  assetBaseUrl: string | undefined,
): Project['variables'] {
  const nativeMatch3 = resolveNativeMatch3AssetUrls(variables.nativeMatch3, assetBaseUrl);
  return nativeMatch3 === variables.nativeMatch3 ? variables : { ...variables, nativeMatch3 };
}

export function resolveProjectAssetUrlsForRuntime(project: Project, assetBaseUrl: string | undefined): Project {
  const normalizedProject = normalizeNativeMatch3SceneShell(project);
  const root = resolveSceneLayerAssetUrls(normalizedProject.scene_graph.root, assetBaseUrl);
  const slotMap = resolveSlotMapAssetUrls(normalizedProject.slot_map, assetBaseUrl);
  const assets = normalizedProject.assets.map((asset) => resolveAssetRefUrl(asset, assetBaseUrl));
  const variables = resolveProjectVariablesForRuntime(normalizedProject.variables, assetBaseUrl);
  const assetsChanged = assets.some((asset, index) => asset !== normalizedProject.assets[index]);
  if (
    normalizedProject === project
    && root === project.scene_graph.root
    && slotMap === project.slot_map
    && !assetsChanged
    && variables === project.variables
  ) return project;
  return {
    ...normalizedProject,
    scene_graph: root === normalizedProject.scene_graph.root
      ? normalizedProject.scene_graph
      : { ...normalizedProject.scene_graph, root },
    slot_map: slotMap,
    assets: assetsChanged ? assets : normalizedProject.assets,
    variables,
  };
}

export function collectNativeMatch3AssetUrls(project: Project): string[] {
  const parsed = validateMatch3NativeRuntimePayloadV1(project.variables.nativeMatch3);
  if (!parsed.ok) return [];
  return [...new Set([
    ...parsed.value.level.tiles.map((tile) => tile.assetUrl),
    parsed.value.skin.slots.background,
    parsed.value.skin.slots.boardPanel,
    ...parsed.value.skin.slots.tileTypes,
    ...parsed.value.skin.slots.objectiveStages,
    parsed.value.skin.slots.tutorialHand,
    parsed.value.skin.slots.hudPanel,
    parsed.value.skin.slots.ctaButton,
    ...(parsed.value.skin.slots.logo === undefined ? [] : [parsed.value.skin.slots.logo]),
    ...(parsed.value.skin.slots.reward === undefined ? [] : [parsed.value.skin.slots.reward]),
  ])];
}

export function createProjectRenderedPayload(renderModel: RenderModel): RuntimeRenderedPayload {
  return {
    projectId: renderModel.projectId,
    layerCount: renderModel.layerCount,
    assetCount: renderModel.assetUrls.length,
  };
}

export function createRenderModelKey(renderModel: RenderModel): string {
  return JSON.stringify(renderModel);
}

function collectRenderNodeIds(node: RenderNode, output: string[] = []): string[] {
  output.push(node.id);
  if (node.type === 'container') {
    for (const child of node.children) collectRenderNodeIds(child, output);
  }
  return output;
}

function collectMountedRenderNodeIds(node: RenderNode, output: string[] = []): string[] {
  if (!node.visible) return output;
  output.push(node.id);
  if (node.type === 'container') {
    for (const child of node.children) collectMountedRenderNodeIds(child, output);
  }
  return output;
}

export function reportRenderModelErrors(
  renderModel: RenderModel,
  onRuntimeError: (code: string, message: string) => void,
): void {
  for (const error of renderModel.errors) {
    onRuntimeError(error.code, error.message);
  }
}

export function RuntimeCanvas({
  project,
  viewport,
  highlightedLayerIds,
  onLayerSelected,
  onRuntimeError,
  onRuntimeAnalytics,
  onRuntimeInput,
  onRuntimeControls,
  onRuntimeProbeControls,
  onProjectRendered,
  onRuntimeFacts,
  assetBaseUrl,
}: RuntimeCanvasProps) {
  const [readyAssetKey, setReadyAssetKey] = React.useState<string | null>(null);
  const [assetLoadErrorCount, setAssetLoadErrorCount] = React.useState(0);
  const onRuntimeErrorRef = React.useRef(onRuntimeError);
  const lastReportedStateRef = React.useRef<ReportedRuntimeState | null>(null);

  React.useEffect(() => {
    onRuntimeErrorRef.current = onRuntimeError;
  }, [onRuntimeError]);

  const runtimeProject = React.useMemo(
    () => (project ? resolveProjectAssetUrlsForRuntime(project, assetBaseUrl) : null),
    [assetBaseUrl, project],
  );

  const renderModel: RenderModel | null = React.useMemo(
    () => (runtimeProject ? compileProjectRenderModel(runtimeProject) : null),
    [runtimeProject],
  );

  const renderModelKey = React.useMemo(
    () => (renderModel ? createRenderModelKey(renderModel) : null),
    [renderModel],
  );

  const assetUrls = React.useMemo(
    () => (renderModel && runtimeProject
      ? [...new Set([
        ...collectRenderAssetUrls(renderModel),
        ...runtimeProject.assets.map((asset) => asset.url),
        ...collectNativeMatch3AssetUrls(runtimeProject),
      ])]
      : []),
    [renderModel, runtimeProject],
  );

  const layerIds = React.useMemo(
    () => (renderModel ? collectRenderNodeIds(renderModel.root) : []),
    [renderModel],
  );

  const assetsReady = renderModelKey !== null && readyAssetKey === renderModelKey;
  const mountedLayerIds = React.useMemo(
    () => (renderModel && assetsReady && renderModel.errors.length === 0
      ? collectMountedRenderNodeIds(renderModel.root)
      : []),
    [assetsReady, renderModel],
  );

  React.useEffect(() => {
    onRuntimeFacts({
      layerIds,
      mountedLayerIds,
      assetUrls,
      errorCount: (renderModel?.errors.length ?? 0) + assetLoadErrorCount,
    });
  }, [assetLoadErrorCount, assetUrls, layerIds, mountedLayerIds, onRuntimeFacts, renderModel?.errors.length]);

  React.useEffect(() => {
    setAssetLoadErrorCount(0);
    if (!runtimeProject || !renderModel || renderModelKey === null) {
      setReadyAssetKey(null);
      lastReportedStateRef.current = null;
      return;
    }

    if (renderModel.errors.length > 0) {
      setReadyAssetKey(null);
      const lastReportedState = lastReportedStateRef.current;
      if (lastReportedState?.kind !== 'error' || lastReportedState.key !== renderModelKey) {
        reportRenderModelErrors(renderModel, onRuntimeErrorRef.current);
        lastReportedStateRef.current = { kind: 'error', key: renderModelKey };
      }
      return;
    }

    if (assetUrls.length === 0) {
      setReadyAssetKey(renderModelKey);
      return;
    }

    let cancelled = false;
    setReadyAssetKey(null);
    void preloadPixiAssets(assetUrls)
      .then(() => {
        if (!cancelled) setReadyAssetKey(renderModelKey);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        const message = error instanceof Error ? error.message : String(error);
        setAssetLoadErrorCount(1);
        onRuntimeErrorRef.current('asset-load-failed', message);
      });

    return () => {
      cancelled = true;
    };
  }, [assetUrls, runtimeProject, renderModel, renderModelKey]);

  React.useEffect(() => {
    const assetsReady = renderModelKey !== null && readyAssetKey === renderModelKey;
    if (!renderModel || renderModelKey === null || !assetsReady) return;
    const lastReportedState = lastReportedStateRef.current;
    if (lastReportedState?.kind === 'rendered' && lastReportedState.key === renderModelKey) return;
    lastReportedStateRef.current = { kind: 'rendered', key: renderModelKey };
    onProjectRendered(createProjectRenderedPayload(renderModel));
  }, [onProjectRendered, readyAssetKey, renderModel, renderModelKey]);

  if (!runtimeProject || !renderModel || !assetsReady) {
    return <div className="runtime-root">Loading...</div>;
  }

  return (
    <div className="runtime-root">
      <PixiRuntimeCanvas
        renderModel={renderModel}
        project={runtimeProject}
        viewport={viewport}
        highlightedLayerIds={highlightedLayerIds}
        assetBaseUrl={assetBaseUrl}
        onLayerSelected={onLayerSelected}
        onRuntimeError={onRuntimeError}
        onRuntimeAnalytics={onRuntimeAnalytics}
        onRuntimeInput={onRuntimeInput}
        onRuntimeControls={onRuntimeControls}
        onRuntimeProbeControls={onRuntimeProbeControls}
      />
    </div>
  );
}
